import { PropertyValueMap, html, nothing, CSSResultGroup } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import type { ImageGalleryThumbnailTargetType } from '../image-gallery-thumbnail-item/image-gallery-thumbnail-item.model';
import styles from './image-gallery-thumbnail-item.styles';

/**
 * @slot - The component content
 * @slot custom-link - The component's custom link slot
 * @csspart base - The component's base wrapper.
 * @csspart thumbnail-item-link The component's thumbnail item link wrapper
 * @csspart thumbnail-item-custom-link - The component's thumbnail item custom link wrapper
 * @csspart thumbnail-item-link-slot - The component's thumbnail item  link slot
 * @csspart thumbnail-item-dot - The component's thumbnail item  link dot in small variant
 * @csspart thumbnail-image - The component's thumbnail item  image
 */
export class BlocksImageGalleryThumbnailItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   *  The alternative text to be displayed if the image fails to load.
   */
  @property({ type: String }) alt: string | undefined;

  /**
   * The URL or path to the thumbnail image.
   */
  @property({ type: String }) src: string | undefined;

  /**
   * Index to track tab
   */
  @property({ type: Number })
  index: number | undefined;

  /**
   * Set Active property
   */
  @property({ type: Boolean })
  isActive = false;

  /**
   * Set small property
   */
  @property({ type: Boolean })
  isSmall = false;

  /**
   * href property
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Target property
   */
  @property({ type: String })
  target: ImageGalleryThumbnailTargetType;

  /**
   * thumbnail item ariaControls
   * 1) Set to the id to connect the tab trigger to the gallery item
   *
   */
  @property({ type: String })
  ariaControls: string | undefined;

  /**
   * Aria labelled by property
   * 1) Used to connect thumbnail trigger and gallery item for accessibility
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   * ariaLabel For customized label announcements for tab.
   */
  @property({ type: String })
  arialabel: string | undefined;

  @query('.cat-c-image-gallery-thumbnail-item__link') private thumbnailItem!: HTMLElement;

  protected slots = new SlotController(this, 'custom-link', null);

  protected firstUpdated(): void {
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', '-1');
    this.setAttribute('aria-selected', 'false');
    this.setAttribute('aria-label', this.arialabel ?? '');
    if (this.slots.isEmpty()) {
      this.removeAttribute('tabindex');
    }
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('ariaControls') && this.ariaControls) {
      this.setAttribute('aria-controls', this.ariaControls);
    }
    if (!this.slots.isEmpty() && _changedProperties.has('isActive')) {
      this.setAttribute('tabindex', this.isActive ? '0' : '-1');
      this.setAttribute('aria-selected', `${this.isActive}`);
    }
    if (_changedProperties.has('ariaLabelledBy') && this.ariaLabelledBy) {
      this.setAttribute('id', this.ariaLabelledBy);
    }
    if (_changedProperties.has('arialabel') && this.arialabel) {
      this.setAttribute('aria-label', this.arialabel);
    }
  }

  /**
   * Handle on keydown/click
   * 1) If key selected is enter/clicked, navigate to the custom link
   */

  private handleCustomLinkKeyEvent(e: KeyboardEvent | MouseEvent) {
    if (this.slots.hasSlotted('custom-link')) {
      if ((e.type === 'click' || (e instanceof KeyboardEvent && e.key === KEYS.ENTER)) && this._children) {
        this._children[0].click();
      }
    }
    this.focus();
  }

  override focus() {
    this.thumbnailItem?.focus();
  }

  render() {
    const componentClassNames = this.componentClassNames('cat-c-image-gallery-thumbnail-item', {
      'cat-is-active': this.isActive,
      'cat-c-image-gallery-thumbnail-item--small': this.isSmall,
      'cat-c-image-gallery-thumbnail-item--has-default': !this.isSmall && !this.slots.isEmpty()
    });

    return html`
      <li part="base" role="none" class="${componentClassNames}">
        ${this.slots.isEmpty('custom-link')
          ? html`
              <a
                part="thumbnail-item-link"
                tabindex="-1"
                class="cat-c-image-gallery-thumbnail-item__link"
                href=${ifDefined(this.href)}
                target=${ifDefined(this.target)}
              >
                ${!this.isSmall && this.slots.isEmpty()
                  ? html`
                      <img
                        class="cat-c-image-gallery-thumbnail-item__image"
                        part="thumbnail-image"
                        src=${ifDefined(this.src)}
                        alt=${ifDefined(this.alt)}
                        loading="lazy"
                      />
                    `
                  : html`${nothing}`}
                <slot part="thumbnail-item-link-slot"></slot>
                ${this.isSmall ? html`<span part="thumbnail-item-dot" class="cat-c-image-gallery-thumbnail-item__dot"></span> ` : html`${nothing}`}
              </a>
            `
          : html`
              <div
                part="thumbnail-item-custom-link"
                class="cat-c-image-gallery-thumbnail-item__link"
                tabindex=${0}
                @click=${this.handleCustomLinkKeyEvent}
                @keydown=${this.handleCustomLinkKeyEvent}
              >
                <slot part="default" name="custom-link" tabindex=${-1}></slot>
              </div>
            `}
      </li>
    `;
  }
}

if (customElements.get('cat-image-gallery-thumbnail-item') === undefined) {
  customElements.define('cat-image-gallery-thumbnail-item', BlocksImageGalleryThumbnailItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-image-gallery-thumbnail-item': BlocksImageGalleryThumbnailItem;
  }
}
